import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import PaymentGate from './PaymentGate';
import '../styles/design-system.css';

export default function MobileAppPromotion() {
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState('free');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [deviceType, setDeviceType] = useState('unknown');

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        const subscriptionStatus = attributes['custom:subscriptionStatus'] || 'free';
        setUserSubscriptionStatus(subscriptionStatus);
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    // Detect device type
    const detectDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      if (/android/.test(userAgent)) {
        setDeviceType('android');
      } else if (/iphone|ipad|ipod/.test(userAgent)) {
        setDeviceType('ios');
      } else {
        setDeviceType('web');
      }
    };

    loadUserProfile();
    detectDevice();
  }, []);

  const handleDownloadApp = () => {
    if (deviceType === 'android') {
      window.open('https://play.google.com/store/apps/details?id=com.gradedwash.app', '_blank');
    } else if (deviceType === 'ios') {
      window.open('https://apps.apple.com/app/gradedwash/id123456789', '_blank');
    } else {
      setShowDownloadModal(true);
    }
  };

  const getDownloadLink = () => {
    if (deviceType === 'android') {
      return 'https://play.google.com/store/apps/details?id=com.gradedwash.app';
    } else if (deviceType === 'ios') {
      return 'https://apps.apple.com/app/gradedwash/id123456789';
    }
    return '#';
  };

  const mobileFeatures = [
    {
      icon: '📱',
      title: 'Real-time Messaging',
      description: 'Chat with travel companions instantly with push notifications'
    },
    {
      icon: '📍',
      title: 'GPS Meet Discovery',
      description: 'Find local meetups and travelers near you automatically'
    },
    {
      icon: '🤖',
      title: 'AI Travel Assistant',
      description: 'Get personalized recommendations and conversation starters'
    },
    {
      icon: '🛡️',
      title: 'Safety Features',
      description: 'Real-time safety alerts and location sharing with trusted contacts'
    },
    {
      icon: '📸',
      title: 'Photo Sharing',
      description: 'Share travel photos and experiences with your matches'
    },
    {
      icon: '🔔',
      title: 'Smart Notifications',
      description: 'Get notified about matches, events, and travel opportunities'
    }
  ];

  const renderMobileAppSection = () => {
    const isPaidUser = userSubscriptionStatus === 'paid' || userSubscriptionStatus === 'premium';

    if (!isPaidUser) {
      return (
        <PaymentGate
          feature="mobile-app"
          userSubscriptionStatus={userSubscriptionStatus}
          customMessage="Upgrade to access our exclusive mobile app with AI-powered features!"
        >
          <div className="bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl p-8 mb-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📱</div>
              <h2 className="text-3xl font-bold mb-4">GradedWash Mobile App</h2>
              <p className="text-xl opacity-90 mb-6">
                Take your travel companion search on the go with our exclusive mobile app!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {mobileFeatures.slice(0, 4).map((feature, index) => (
                <div key={index} className="bg-white/10 rounded-lg p-4">
                  <div className="text-3xl mb-2">{feature.icon}</div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm opacity-90">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <button className="btn btn-secondary text-lg px-8 py-4">
                Upgrade to Access Mobile App
              </button>
            </div>
          </div>
        </PaymentGate>
      );
    }

    return (
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-8 mb-6">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">📱</div>
          <h2 className="text-3xl font-bold mb-4">GradedWash Mobile App</h2>
          <p className="text-xl opacity-90 mb-6">
            Download our exclusive mobile app for the ultimate travel companion experience!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {mobileFeatures.map((feature, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4 text-center">
              <div className="text-3xl mb-2">{feature.icon}</div>
              <h3 className="font-semibold mb-2">{feature.title}</h3>
              <p className="text-sm opacity-90">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={handleDownloadApp}
            className="btn btn-secondary text-lg px-8 py-4"
          >
            {deviceType === 'android' ? 'Download for Android' :
             deviceType === 'ios' ? 'Download for iOS' :
             'Get Download Links'}
          </button>
        </div>

        {deviceType === 'web' && (
          <div className="mt-6 text-center">
            <p className="text-sm opacity-75 mb-4">
              Scan the QR code or visit the app store on your mobile device
            </p>
            <div className="flex justify-center gap-4">
              <div className="text-center">
                <div className="w-24 h-24 bg-white rounded-lg flex items-center justify-center mb-2">
                  <span className="text-2xl">📱</span>
                </div>
                <p className="text-xs">Android</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-white rounded-lg flex items-center justify-center mb-2">
                  <span className="text-2xl">🍎</span>
                </div>
                <p className="text-xs">iOS</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mobile-app-promotion">
      {renderMobileAppSection()}

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="text-4xl mb-4">📱</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Download GradedWash Mobile App
              </h2>
              <p className="text-gray-600">
                Get the full mobile experience with AI-powered features
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.gradedwash.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <div className="text-2xl mr-3">🤖</div>
                  <div>
                    <h3 className="font-semibold">Android App</h3>
                    <p className="text-sm text-gray-600">Google Play Store</p>
                  </div>
                </div>
                <span className="text-primary">→</span>
              </a>

              <a
                href="https://apps.apple.com/app/gradedwash/id123456789"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <div className="text-2xl mr-3">🍎</div>
                  <div>
                    <h3 className="font-semibold">iOS App</h3>
                    <p className="text-sm text-gray-600">App Store</p>
                  </div>
                </div>
                <span className="text-primary">→</span>
              </a>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">Mobile App Features:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Real-time messaging with push notifications</li>
                <li>• GPS-based local meet discovery</li>
                <li>• AI-powered travel recommendations</li>
                <li>• Safety alerts and location sharing</li>
                <li>• Offline personality test completion</li>
                <li>• Photo sharing and profile management</li>
              </ul>
            </div>

            <button
              onClick={() => setShowDownloadModal(false)}
              className="btn btn-secondary w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 